import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { 
  Shield, Clock, User, MapPin, Monitor, AlertTriangle,
  CheckCircle, XCircle, Search, Filter, RefreshCw, Ban,
  Eye, LogIn, Key, Activity
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import { format } from "date-fns";

const AdminSecurity = () => {
  const [loading, setLoading] = useState(true);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [newBlockIP, setNewBlockIP] = useState("");

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [activityRes, loginRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/activity-logs`, getAuthConfig()),
        axios.get(`${API}/admin/login-history`, getAuthConfig()),
        axios.get(`${API}/admin/blocked-ips`, getAuthConfig())
      ]);
      setActivityLogs(activityRes.data.logs || []);
      setLoginHistory(loginRes.data.history || []);
      setBlockedIPs(blockedRes.data.ips || []);
    } catch (error) {
      console.error("Failed to fetch security data:", error);
    } finally {
      setLoading(false);
    }
  };

  const blockIP = async () => {
    if (!newBlockIP) return;
    try {
      await axios.post(`${API}/admin/block-ip`, { ip: newBlockIP }, getAuthConfig());
      toast.success("IP blocked");
      setNewBlockIP("");
      fetchData();
    } catch (error) {
      toast.error("Failed to block IP");
    }
  };

  const unblockIP = async (ip) => {
    try {
      await axios.delete(`${API}/admin/block-ip/${ip}`, getAuthConfig());
      toast.success("IP unblocked");
      fetchData();
    } catch (error) {
      toast.error("Failed to unblock IP");
    }
  };

  const forceLogout = async (userId) => {
    if (!confirm("Force logout this user?")) return;
    try {
      await axios.post(`${API}/admin/force-logout/${userId}`, {}, getAuthConfig());
      toast.success("User logged out");
    } catch (error) {
      toast.error("Failed to logout user");
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "login": return <LogIn className="w-4 h-4 text-green-400" />;
      case "logout": return <LogIn className="w-4 h-4 text-gray-400" />;
      case "password_change": return <Key className="w-4 h-4 text-yellow-400" />;
      case "settings_update": return <Activity className="w-4 h-4 text-blue-400" />;
      default: return <Activity className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const filteredLogs = activityLogs.filter(log => {
    const matchesSearch = log.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          log.action?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || log.action === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6" data-testid="admin-security-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Security & Access</h1>
          <p className="text-muted-foreground">Monitor activity, login history, and manage access controls</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="border-theme text-foreground">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Security Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Successful Logins</p>
              <p className="text-xl font-bold text-foreground">{loginHistory.filter(l => l.success).length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Failed Logins</p>
              <p className="text-xl font-bold text-foreground">{loginHistory.filter(l => !l.success).length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
              <Ban className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Blocked IPs</p>
              <p className="text-xl font-bold text-foreground">{blockedIPs.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="glass-card border-theme">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Events</p>
              <p className="text-xl font-bold text-foreground">{activityLogs.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="activity" className="space-y-4">
        <TabsList className="bg-theme-surface border border-theme max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="activity" className="data-[state=active]:bg-muted text-foreground">
            <Activity className="w-4 h-4 mr-2" /> Activity Logs
          </TabsTrigger>
          <TabsTrigger value="logins" className="data-[state=active]:bg-muted text-foreground">
            <LogIn className="w-4 h-4 mr-2" /> Login History
          </TabsTrigger>
          <TabsTrigger value="blocked" className="data-[state=active]:bg-muted text-foreground">
            <Ban className="w-4 h-4 mr-2" /> Blocked IPs
          </TabsTrigger>
        </TabsList>

        {/* Activity Logs Tab */}
        <TabsContent value="activity">
          <Card className="glass-card border-theme">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground">Activity Logs</CardTitle>
                <div className="flex flex-wrap gap-2">
                  <div className="relative flex-1 min-w-[180px] sm:flex-none">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-theme-surface border-theme text-foreground pl-9 w-full sm:w-64"
                    />
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-36 bg-theme-surface border-theme text-foreground">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-theme">
                      <SelectItem value="all">All Actions</SelectItem>
                      <SelectItem value="login">Login</SelectItem>
                      <SelectItem value="logout">Logout</SelectItem>
                      <SelectItem value="settings_update">Settings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredLogs.map((log, i) => (
                  <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      {getActionIcon(log.action)}
                      <div>
                        <p className="text-foreground text-sm">{log.user_email || "Unknown"}</p>
                        <p className="text-muted-foreground text-xs">{log.action} • {log.details || "-"}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-muted-foreground text-xs">
                        {log.created_at ? format(new Date(log.created_at), "MMM dd, h:mm a") : "-"}
                      </p>
                      <p className="text-muted-foreground text-xs">{log.ip || "-"}</p>
                    </div>
                  </div>
                ))}
                {filteredLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No activity logs</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Login History Tab */}
        <TabsContent value="logins">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">Login History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-theme">
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">User</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">IP Address</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Device</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Time</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Status</th>
                      <th className="text-left text-muted-foreground text-sm py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.map((login, i) => (
                      <tr key={i} className="border-b border-theme hover:bg-muted/50">
                        <td className="py-3 px-4 text-foreground">{login.user_email || "-"}</td>
                        <td className="py-3 px-4 text-muted-foreground">{login.ip || "-"}</td>
                        <td className="py-3 px-4 text-muted-foreground text-sm">{login.device || "-"}</td>
                        <td className="py-3 px-4 text-muted-foreground">
                          {login.created_at ? format(new Date(login.created_at), "MMM dd, h:mm a") : "-"}
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={login.success ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                            {login.success ? "Success" : "Failed"}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {login.user_id && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => forceLogout(login.user_id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              Force Logout
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {loginHistory.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No login history</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Blocked IPs Tab */}
        <TabsContent value="blocked">
          <Card className="glass-card border-theme">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground">Blocked IP Addresses</CardTitle>
                <div className="flex gap-2">
                  <Input
                    value={newBlockIP}
                    onChange={(e) => setNewBlockIP(e.target.value)}
                    placeholder="IP address..."
                    className="bg-theme-surface border-theme text-foreground w-48"
                  />
                  <Button onClick={blockIP} className="bg-red-600 text-white">
                    <Ban className="w-4 h-4 mr-2" /> Block IP
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {blockedIPs.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-red-500/20 flex items-center justify-center">
                        <Ban className="w-4 h-4 text-red-400" />
                      </div>
                      <div>
                        <p className="text-foreground font-mono">{item.ip}</p>
                        <p className="text-muted-foreground text-xs">{item.reason || "Manually blocked"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-muted-foreground text-xs">
                        {item.blocked_at ? format(new Date(item.blocked_at), "MMM dd, yyyy") : "-"}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => unblockIP(item.ip)}
                        className="text-green-400 hover:text-green-300"
                      >
                        Unblock
                      </Button>
                    </div>
                  </div>
                ))}
                {blockedIPs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No blocked IPs</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSecurity;
